<template>
  <div>
    <b-card>
      <b-card-header class="pb-1">
        <b-card-title>{{ isEdit ? 'تعديل' : 'اضافة' }}</b-card-title>
      </b-card-header>

      <b-card-body>
        <b-form @submit.prevent="handleSubmit">
          <b-row>

            <!-- Name -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                :label="$t('serviceTypes.fields.name') + ' *'"
                label-for="name"
              >
                <b-form-input
                  id="name"
                  v-model="form.name"
                  :placeholder="$t('serviceTypes.placeholders.name')"
                  :state="errors.name ? false : null"
                />
                <b-form-invalid-feedback v-if="errors.name">
                  {{ errors.name[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Arabic Name -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                :label="$t('serviceTypes.fields.name_ar')"
                label-for="name_ar"
              >
                <b-form-input
                  id="name_ar"
                  v-model="form.name_ar"
                  :placeholder="$t('serviceTypes.placeholders.name_ar')"
                  dir="rtl"
                  :state="errors.name_ar ? false : null"
                />
                <b-form-invalid-feedback v-if="errors.name_ar">
                  {{ errors.name_ar[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label-for="gl_activity"
              >
                <template #label>
                  {{ $t('serviceTypes.fields.gl_activity') }}
                  <b-badge
                    v-if="showInferredBadge"
                    variant="light-warning"
                    class="ml-50"
                  >
                    {{ $t('serviceTypes.gl.inferred') }}
                  </b-badge>
                </template>
                <v-select
                  id="gl_activity"
                  :value="form.gl_activity || null"
                  :options="activityOptions"
                  :reduce="option => option.value"
                  label="label_ar"
                  :placeholder="$t('serviceTypes.gl.placeholder')"
                  :dir="isRtl ? 'rtl' : 'ltr'"
                  class="gl-activity-select"
                  :class="{ 'is-invalid': errors.gl_activity }"
                  @input="onActivityPick"
                >
                  <template #option="option">
                    <div class="gl-activity-option">
                      <div>{{ option.label_ar }}</div>
                      <small class="text-muted gl-activity-desc">{{ option.description_ar }}</small>
                    </div>
                  </template>
                </v-select>
                <b-form-invalid-feedback
                  v-if="errors.gl_activity"
                  :state="false"
                >
                  {{ $t('serviceTypes.gl.invalid') }}
                </b-form-invalid-feedback>
                <div
                  v-if="selectedActivity"
                  class="gl-activity-help mt-50"
                >
                  <div>{{ selectedActivity.description_ar }}</div>
                  <div class="small text-muted mt-50">
                    {{ $t('serviceTypes.gl.accounts', { revenue: selectedActivity.revenue_code, cost: selectedActivity.cost_code }) }}
                  </div>
                  <div class="mt-50">
                    <b-badge
                      v-for="key in capabilityKeys(selectedActivity)"
                      :key="key"
                      variant="light-primary"
                      class="mr-50 mb-50"
                    >
                      {{ $t('serviceTypes.gl.capabilities.' + key) }}
                    </b-badge>
                  </div>
                </div>
                <small
                  v-else-if="!isEdit || glActivityCleared"
                  class="text-muted d-block mt-50"
                >
                  {{ $t('serviceTypes.gl.auto_note') }}
                </small>
                <b-button
                  v-if="isEdit"
                  variant="link"
                  class="px-0"
                  @click="resetActivity"
                >
                  {{ $t('serviceTypes.gl.reset') }}
                </b-button>
              </b-form-group>
            </b-col>

            <b-col
              v-if="isEdit && effectiveActivity"
              cols="12"
            >
              <div class="d-flex flex-wrap align-items-center mb-1">
                <gl-activity-chip
                  :activity="effectiveActivity"
                  :inferred="showInferredBadge"
                  class="mr-1 mb-50"
                />
                <span class="text-muted mb-50 mr-1">{{ effectiveActivity.description_ar }}</span>
                <span class="mr-1 mb-50">{{ $t('serviceTypes.gl.revenue_account', { code: effectiveActivity.revenue_code }) }}</span>
                <span class="mr-1 mb-50">{{ $t('serviceTypes.gl.cost_account', { code: effectiveActivity.cost_code }) }}</span>
                <span class="mb-50 mr-1">{{ $t('serviceTypes.fields.projects_count') }}: {{ projectsCount }}</span>
                <b-badge
                  v-for="key in capabilityKeys(effectiveActivity)"
                  :key="key"
                  variant="light-primary"
                  class="mr-50 mb-50"
                >
                  {{ $t('serviceTypes.gl.capabilities.' + key) }}
                </b-badge>
              </div>
            </b-col>

            <!-- Description -->
            <b-col cols="12">
              <b-form-group
                :label="$t('serviceTypes.fields.description')"
                label-for="description"
              >
                <b-form-textarea
                  id="description"
                  v-model="form.description"
                  :placeholder="$t('serviceTypes.placeholders.description')"
                  rows="3"
                  :state="errors.description ? false : null"
                />
                <b-form-invalid-feedback v-if="errors.description">
                  {{ errors.description[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Color -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                :label="$t('serviceTypes.fields.color')"
                label-for="color"
              >
                <div class="d-flex align-items-center">
                  <b-form-input
                    id="color"
                    v-model="form.color"
                    type="color"
                    class="mr-1"
                    style="width: 60px; height: 38px;"
                    :state="errors.color ? false : null"
                  />
                  <b-form-input
                    v-model="form.color"
                    :placeholder="$t('serviceTypes.placeholders.color')"
                    :state="errors.color ? false : null"
                  />
                </div>
                <b-form-invalid-feedback v-if="errors.color">
                  {{ errors.color[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Icon -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                :label="$t('serviceTypes.fields.icon')"
                label-for="icon"
              >
                <b-input-group>
                  <b-form-input
                    id="icon"
                    v-model="form.icon"
                    :placeholder="$t('serviceTypes.placeholders.icon')"
                    :state="errors.icon ? false : null"
                  />
                  <b-input-group-append>
                    <b-button variant="outline-secondary">
                      <feather-icon
                        v-if="form.icon"
                        :icon="form.icon"
                      />
                      <feather-icon
                        v-else
                        icon="HelpCircleIcon"
                      />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <b-form-invalid-feedback v-if="errors.icon">
                  {{ errors.icon[0] }}
                </b-form-invalid-feedback>
                <small class="text-muted">{{ $t('serviceTypes.messages.feather_icon_name') }}</small>
              </b-form-group>
            </b-col>

            <!-- Order -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                :label="$t('serviceTypes.fields.order')"
                label-for="order"
              >
                <b-form-input
                  id="order"
                  v-model.number="form.order"
                  type="number"
                  min="0"
                  :placeholder="$t('serviceTypes.placeholders.order')"
                  :state="errors.order ? false : null"
                />
                <b-form-invalid-feedback v-if="errors.order">
                  {{ errors.order[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Is Active -->
            <b-col cols="12">
              <b-form-group>
                <b-form-checkbox
                  v-model="form.is_active"
                  switch
                >
                  {{ $t('common.active') }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <!-- Services -->
            <b-col
              v-if="!loadServices"
              cols="12"
            >
              <b-form-group
                label="الخدمات"
                label-for="services"
              >
                <ul>
                  <ServiceTreeItem
                    v-for="service in services"
                    :key="service.id"
                    v-model="form.services_ids"
                    :service="service"
                  />
                </ul>
              </b-form-group>
            </b-col>
            <!-- Attachment lookup assignments (slot + lookup per row) -->
            <b-col cols="12">
              <b-form-group
                :label="$t('serviceTypes.fields.attachment_lookups')"
                label-for="attachment-assignments"
              >
                <small class="text-muted d-block mb-1">
                  {{ $t('serviceTypes.messages.attachment_assignments_help') }}
                </small>
                <div id="attachment-assignments">
                  <b-row
                    v-for="row in attachmentAssignmentRows"
                    :key="row.rowKey"
                    class="align-items-start mb-2"
                  >
                    <b-col
                      cols="12"
                      md="5"
                    >
                      <label class="d-block small text-muted mb-25">{{ $t('serviceTypes.fields.attachment_department_slot') }}</label>
                      <b-form-select
                        v-model="row.attachment_department_id"
                        :options="departmentSlotSelectOptions"
                        :disabled="loadingDepartmentSlots"
                        :state="assignmentRowState(row)"
                      />
                    </b-col>
                    <b-col
                      cols="12"
                      md="5"
                    >
                      <label class="d-block small text-muted mb-25">{{ $t('serviceTypes.fields.attachment_lookup') }}</label>
                      <b-form-select
                        v-model="row.attachment_lookup_ids"
                        :options="attachmentLookupOptions"
                        value-field="value"
                        text-field="label"
                        multiple
                        :select-size="Math.min(Math.max(attachmentLookupOptions.length, 4), 8)"
                        :disabled="loadingAttachmentLookups"
                        :state="assignmentRowState(row)"
                      />
                    </b-col>
                    <b-col
                      cols="12"
                      md="2"
                      class="mb-1"
                    >
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        class="mt-1 mt-md-0"
                        @click="removeAttachmentRow(row.rowKey)"
                      >
                        <feather-icon icon="TrashIcon" />
                      </b-button>
                    </b-col>
                  </b-row>
                  <div class="d-flex flex-wrap align-items-center mt-1">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="mr-1 mb-50"
                      @click="addAttachmentRow"
                    >
                      <feather-icon
                        icon="PlusIcon"
                        class="mr-25"
                      />
                      {{ $t('serviceTypes.messages.add_attachment_assignment') }}
                    </b-button>
                    <small
                      v-if="loadingAttachmentLookups || loadingDepartmentSlots"
                      class="text-muted"
                    >
                      {{ $t('common.loading') }}
                    </small>
                    <small
                      v-else-if="!attachmentLookupOptions.length"
                      class="text-muted"
                    >
                      {{ $t('serviceTypes.placeholders.attachment_lookups') }}
                    </small>
                  </div>
                </div>
                <small
                  v-if="assignmentClientError"
                  class="text-danger d-block mt-50"
                >{{ assignmentClientError }}</small>
                <b-form-invalid-feedback
                  v-else-if="firstAttachmentAssignmentApiError"
                  :state="false"
                  class="d-block"
                >
                  {{ firstAttachmentAssignmentApiError }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <!-- Buttons -->
            <b-col cols="12">
              <b-button
                type="submit"
                variant="primary"
                :disabled="loading"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ isEdit ? $t('common.update') : $t('common.create') }}</span>
              </b-button>
              <b-button
                variant="outline-secondary"
                class="ml-1"
                :to="{ name: 'service-types-list' }"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('common.cancel') }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>

      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
  BInputGroup,
  BInputGroupAppend,
  BBadge,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { parseExtensions } from '@/utils/attachmentLookup'
import { loadGlActivityOptions, getServiceType } from '@/api/services/serviceTypes'
import ServiceTreeItem from './ServiceTreeItem.vue'
import GlActivityChip from './GlActivityChip.vue'

export default {
  name: 'ServiceTypesForm',
  components: {
    ServiceTreeItem,
    GlActivityChip,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    BInputGroup,
    BInputGroupAppend,
    BBadge,
    vSelect,
  },
  data() {
    return {
      form: {
        name: '',
        name_ar: '',
        description: '',
        color: '#FF5733',
        icon: '',
        order: 0,
        is_active: true,
        services_ids: [],
        gl_activity: '',
      },
      activityOptions: [],
      originalGlActivity: null,
      effectiveActivity: null,
      glActivityTouched: false,
      glActivityCleared: false,
      activityWasInferred: false,
      projectsCount: 0,
      attachmentLookupOptions: [],
      departmentSlotOptions: [],
      attachmentAssignmentRows: [],
      attachmentRowKeySeq: 0,
      loadingAttachmentLookups: false,
      loadingDepartmentSlots: false,
      assignmentClientError: '',
      errors: {},
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      services: 'services/serviceTree',
      loadServices: 'services/loading',
    }),
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    isEdit() {
      return !!this.$route.params.id
    },
    departmentSlotSelectOptions() {
      const placeholder = {
        value: null,
        text: this.$t('serviceTypes.placeholders.select_department_slot'),
        disabled: true,
      }
      const opts = (this.departmentSlotOptions || []).map(o => ({
        value: o.id,
        text: this.getDepartmentSlotLabel(o),
      }))
      return [placeholder, ...opts]
    },
    firstAttachmentAssignmentApiError() {
      const e = this.errors
      if (!e || typeof e !== 'object') return ''
      const direct = e.attachment_lookup_assignments
      if (Array.isArray(direct) && direct[0]) return direct[0]
      const nestedKey = Object.keys(e).find(k => k.startsWith('attachment_lookup_assignments.'))
      if (nestedKey && Array.isArray(e[nestedKey]) && e[nestedKey][0]) return e[nestedKey][0]
      return ''
    },
    selectedActivity() {
      return this.activityOptions.find(option => option.value === this.form.gl_activity) || null
    },
    showInferredBadge() {
      return this.isEdit && this.activityWasInferred && !this.glActivityTouched && !this.glActivityCleared
    },
  },

  mounted() {
    this.getServices()
    this.loadAttachmentLookups()
    this.loadDepartmentSlots()
    this.loadActivityOptions()
    if (!this.isEdit && !this.attachmentAssignmentRows.length) {
      this.addAttachmentRow()
    }
  },
  created() {
    if (this.isEdit) {
      this.loadServiceType()
    }
  },
  methods: {
    ...mapActions('serviceTypes', [
      'fetchServiceType',
      'createServiceType',
      'updateServiceType',
    ]),
    async loadActivityOptions() {
      try {
        this.activityOptions = await loadGlActivityOptions()
      } catch (error) {
        this.activityOptions = []
      }
    },
    onActivityPick(value) {
      const next = value || ''
      if (next === (this.form.gl_activity || '')) return
      this.form.gl_activity = next
      this.glActivityTouched = true
      this.glActivityCleared = false
    },
    capabilityKeys(option) {
      const caps = (option && option.capabilities) || {}
      return ['standard_invoices', 'government_fees', 'contracts', 'revenue_accruals'].filter(key => caps[key])
    },
    capabilityWarnings(fromOption, toOption) {
      const from = (fromOption && fromOption.capabilities) || {}
      const to = (toOption && toOption.capabilities) || {}
      const warnings = []
      if (from.standard_invoices && !to.standard_invoices) warnings.push(this.$t('serviceTypes.gl.warn.standard_invoices'))
      if (!from.contracts && to.contracts) warnings.push(this.$t('serviceTypes.gl.warn.contracts_on'))
      if (from.contracts && !to.contracts) warnings.push(this.$t('serviceTypes.gl.warn.contracts_off'))
      if (from.government_fees && !to.government_fees) warnings.push(this.$t('serviceTypes.gl.warn.government_fees'))
      if (from.revenue_accruals && !to.revenue_accruals) warnings.push(this.$t('serviceTypes.gl.warn.revenue_accruals'))
      return warnings
    },
    resetActivity() {
      this.form.gl_activity = ''
      this.glActivityCleared = true
      this.glActivityTouched = true
    },
    activityByValue(value) {
      return this.activityOptions.find(option => option.value === value) || null
    },
    activityAccounts(option) {
      if (!option) return '—'
      return `${option.revenue_code}/${option.cost_code}`
    },
    async confirmActivityChange(nextValue) {
      const currentValue = this.originalGlActivity || (this.effectiveActivity && this.effectiveActivity.value) || null
      const changed = this.glActivityCleared
        ? this.originalGlActivity != null
        : nextValue !== currentValue
      if (!this.isEdit || !changed) return true
      let count = this.projectsCount
      try {
        const response = await getServiceType(this.$route.params.id)
        const data = response.data || {}
        count = Number(data.projects_count) || 0
        this.projectsCount = count
        if (data.effective_gl_activity) this.effectiveActivity = data.effective_gl_activity
      } catch (error) {
        count = this.projectsCount
      }
      if (count <= 0) return true
      const fromOption = this.effectiveActivity || this.activityByValue(currentValue)
      const toOption = this.glActivityCleared ? null : this.activityByValue(nextValue)
      const warnings = toOption ? this.capabilityWarnings(fromOption, toOption) : []
      const html = [
        `<div>${this.$t('serviceTypes.gl.confirm_body', { count })}</div>`,
        `<div class="mt-1">${this.$t('serviceTypes.gl.confirm_change', {
          from: (fromOption && fromOption.label_ar) || '—',
          from_accounts: this.activityAccounts(fromOption),
          to: toOption ? toOption.label_ar : this.$t('serviceTypes.gl.confirm_auto'),
          to_accounts: toOption ? this.activityAccounts(toOption) : '—',
        })}</div>`,
        toOption && toOption.description_ar ? `<div class="mt-1">${toOption.description_ar}</div>` : '',
        `<div class="mt-1">${this.$t('serviceTypes.gl.confirm_posted')}</div>`,
        ...warnings.map(line => `<div class="mt-1">${line}</div>`),
      ].filter(Boolean).join('')
      const result = await this.$swal({
        title: this.$t('serviceTypes.gl.confirm_title'),
        html,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.cancel') || this.$t('common.no'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      return Boolean(result && result.isConfirmed)
    },
    applyActivityPayload(payload) {
      const value = this.form.gl_activity || null
      if (!this.isEdit) {
        if (value) payload.gl_activity = value
        return payload
      }
      if (this.glActivityCleared || (this.glActivityTouched && !value)) {
        payload.gl_activity = null
        return payload
      }
      if (this.glActivityTouched && value) payload.gl_activity = value
      return payload
    },
    restoreActivity() {
      this.form.gl_activity = this.originalGlActivity || (this.effectiveActivity && this.effectiveActivity.value) || null
      this.glActivityTouched = false
      this.glActivityCleared = false
    },
    getServices() {
      this.$store.dispatch('services/fetchServiceTree', { all: true })
    },

    async loadServiceType() {
      try {
        this.loading = true
        const response = await this.fetchServiceType(this.$route.params.id)
        const data = response.data || {}
        this.form = {
          ...this.form,
          name: data.name ?? '',
          name_ar: data.name_ar ?? '',
          description: data.description ?? '',
          color: data.color || '#FF5733',
          icon: data.icon ?? '',
          order: data.order ?? 0,
          is_active: data.is_active !== false,
          services_ids: this.normalizeIdsArray(data.services_ids),
          gl_activity: data.gl_activity || (data.effective_gl_activity && data.effective_gl_activity.value) || null,
        }
        this.originalGlActivity = data.gl_activity || null
        this.effectiveActivity = data.effective_gl_activity || null
        this.activityWasInferred = Boolean(data.gl_activity_inferred)
        this.glActivityTouched = false
        this.glActivityCleared = false
        this.projectsCount = Number(data.projects_count) || 0
        this.mergeAttachmentLookupOptions(data.attachment_lookups)
        this.attachmentAssignmentRows = this.buildRowsFromApiData(data)
        if (!this.attachmentAssignmentRows.length) {
          this.addAttachmentRow()
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || this.$t('serviceTypes.messages.load_error'),
          },
        })
        this.$router.push({ name: 'service-types-list' })
      } finally {
        this.loading = false
      }
    },
    async handleSubmit() {
      this.errors = {}
      this.assignmentClientError = ''
      const attachmentLookupAssignments = this.buildAssignmentsFromRows()
      if (this.hasDuplicateDepartmentAcrossRows()) {
        this.assignmentClientError = this.$t('serviceTypes.messages.duplicate_department_slot')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: this.assignmentClientError,
          },
        })
        return
      }
      try {
        this.loading = true
        const payload = {
          name: this.form.name,
          name_ar: this.form.name_ar,
          description: this.form.description,
          color: this.form.color,
          icon: this.form.icon,
          order: this.form.order,
          is_active: this.form.is_active,
          services_ids: this.normalizeIdsArray(this.form.services_ids),
          attachment_lookup_assignments: attachmentLookupAssignments,
        }
        this.applyActivityPayload(payload)

        if (this.isEdit) {
          const nextValue = this.form.gl_activity || null
          const confirmed = await this.confirmActivityChange(nextValue)
          if (!confirmed) {
            this.loading = false
            return
          }
          await this.updateServiceType({
            id: this.$route.params.id,
            data: payload,
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('serviceTypes.messages.update_success'),
            },
          })
        } else {
          await this.createServiceType(payload)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('serviceTypes.messages.create_success'),
            },
          })
        }

        this.$router.push({ name: 'service-types-list' })
      } catch (error) {
        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {}
        }
        const apiMessage = error.response?.data?.message || ''
        const contractGuard = /accounting activity cannot be changed/i.test(apiMessage)
          || /must stay contracting/i.test(apiMessage)
        if (contractGuard) this.restoreActivity()
        let text = apiMessage
        if (contractGuard) text = this.$t('serviceTypes.gl.contract_guard')
        else if (this.errors.gl_activity) text = this.$t('serviceTypes.gl.invalid')
        else if (!text) text = this.isEdit ? this.$t('serviceTypes.messages.update_error') : this.$t('serviceTypes.messages.create_error')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text,
          },
        })
      } finally {
        this.loading = false
      }
    },
    normalizeIdsArray(value) {
      if (!Array.isArray(value)) return []
      return value
        .map(id => parseInt(id, 10))
        .filter(Number.isInteger)
    },
    nextRowKey() {
      this.attachmentRowKeySeq += 1
      return this.attachmentRowKeySeq
    },
    addAttachmentRow() {
      this.attachmentAssignmentRows.push({
        rowKey: this.nextRowKey(),
        attachment_department_id: null,
        attachment_lookup_ids: [],
      })
    },
    removeAttachmentRow(key) {
      this.attachmentAssignmentRows = this.attachmentAssignmentRows.filter(r => r.rowKey !== key)
    },
    normalizeInt(value) {
      const n = parseInt(value, 10)
      return Number.isInteger(n) ? n : null
    },
    buildRowsFromApiData(data) {
      const assignments = Array.isArray(data?.attachment_lookup_assignments)
        ? data.attachment_lookup_assignments
        : []
      if (assignments.length) {
        const byDept = new Map()
        assignments.forEach(a => {
          const deptId = this.normalizeInt(a.attachment_department_id)
          const lookupId = this.normalizeInt(a.attachment_lookup_id)
          if (!Number.isInteger(deptId) || !Number.isInteger(lookupId)) return
          if (!byDept.has(deptId)) byDept.set(deptId, [])
          byDept.get(deptId).push(lookupId)
        })
        const rows = []
        byDept.forEach((lookupIds, deptId) => {
          rows.push({
            rowKey: this.nextRowKey(),
            attachment_department_id: deptId,
            attachment_lookup_ids: [...new Set(lookupIds)],
          })
        })
        return rows
      }
      const lookups = Array.isArray(data?.attachment_lookups) ? data.attachment_lookups : []
      if (lookups.length) {
        const byDept = new Map()
        lookups.forEach(l => {
          const deptId = this.normalizeInt(l.attachment_department_id)
          const lookupId = this.normalizeInt(l.id)
          if (!Number.isInteger(lookupId)) return
          const key = Number.isInteger(deptId) ? deptId : '__no_dept__'
          if (!byDept.has(key)) byDept.set(key, { deptId, lookupIds: [] })
          byDept.get(key).lookupIds.push(lookupId)
        })
        const rows = []
        byDept.forEach(({ deptId, lookupIds }) => {
          rows.push({
            rowKey: this.nextRowKey(),
            attachment_department_id: deptId,
            attachment_lookup_ids: [...new Set(lookupIds)],
          })
        })
        return rows
      }
      return []
    },
    buildAssignmentsFromRows() {
      return this.attachmentAssignmentRows.flatMap(row => {
        const deptId = this.normalizeInt(row.attachment_department_id)
        const rawIds = Array.isArray(row.attachment_lookup_ids) ? row.attachment_lookup_ids : []
        const lookupIds = [...new Set(
          rawIds.map(id => this.normalizeInt(id)).filter(Number.isInteger),
        )]
        if (!Number.isInteger(deptId)) return []
        return lookupIds.map(attachment_lookup_id => ({
          attachment_department_id: deptId,
          attachment_lookup_id,
        }))
      })
    },
    hasDuplicateDepartmentAcrossRows() {
      const seen = new Set()
      for (const row of this.attachmentAssignmentRows) {
        const d = this.normalizeInt(row.attachment_department_id)
        if (!Number.isInteger(d)) continue
        if (seen.has(d)) return true
        seen.add(d)
      }
      return false
    },
    assignmentRowState(row) {
      const a = this.normalizeInt(row.attachment_department_id)
      const rawIds = Array.isArray(row.attachment_lookup_ids) ? row.attachment_lookup_ids : []
      const hasLookup = rawIds.some(id => Number.isInteger(this.normalizeInt(id)))
      if (a == null && !hasLookup) return null
      if (a == null && hasLookup) return false
      if (a != null && !hasLookup) return false
      return null
    },
    getDepartmentSlotLabel(slot) {
      if (!slot || typeof slot !== 'object') return ''
      const isArabic = (this.$i18n?.locale || '').startsWith('ar')
      if (isArabic) return slot.name_ar || slot.name || slot.code || `#${slot.id}`
      return slot.name || slot.name_ar || slot.code || `#${slot.id}`
    },
    getAttachmentLookupLabel(lookup) {
      if (!lookup || typeof lookup !== 'object') return ''
      const isArabic = (this.$i18n?.locale || '').startsWith('ar')
      const primaryLabel = isArabic
        ? (lookup.name_ar || lookup.name || lookup.label)
        : (lookup.name || lookup.name_ar || lookup.label)

      const base = primaryLabel || lookup.key || (lookup.id || lookup.value ? `#${lookup.id || lookup.value}` : '')
      if (!base) return ''

      const extensions = parseExtensions(lookup.extensions)
      const extPart = extensions.length ? extensions.join(', ') : ''
      const multiple = lookup.is_multiple === true || lookup.is_multiple === 1 || lookup.is_multiple === 'true'
      const countPart = multiple
        ? this.$t('serviceTypes.fields.multiple_files')
        : this.$t('serviceTypes.fields.single_file')
      const extras = [extPart, countPart].filter(Boolean).join(' · ')
      return extras ? `${base} (${extras})` : base
    },
    mergeAttachmentLookupOptions(lookups = []) {
      if (!Array.isArray(lookups) || !lookups.length) return
      const incoming = lookups.map(item => {
        const rawId = item.id ?? item.value
        const value = Number.isInteger(rawId) ? rawId : parseInt(rawId, 10)
        return {
          value,
          label: this.getAttachmentLookupLabel(item),
          extensions: parseExtensions(item.extensions),
          is_multiple: item.is_multiple === true || item.is_multiple === 1 || item.is_multiple === 'true',
        }
      }).filter(item => Number.isInteger(item.value))
      const byValue = new Map(this.attachmentLookupOptions.map(item => [item.value, item]))
      incoming.forEach(item => byValue.set(item.value, item))
      this.attachmentLookupOptions = [...byValue.values()]
    },
    async loadAttachmentLookups() {
      this.loadingAttachmentLookups = true
      try {
        const response = await this.$store.dispatch('attachmentLookups/lookups', { all: true })
        const data = response?.data
        let lookups = []
        if (Array.isArray(data)) {
          lookups = data
        } else if (Array.isArray(response)) {
          lookups = response
        }
        this.mergeAttachmentLookupOptions(lookups)
      } catch (error) {
        // Keep the form usable even when lookup source is unavailable.
      } finally {
        this.loadingAttachmentLookups = false
      }
    },
    async loadDepartmentSlots() {
      this.loadingDepartmentSlots = true
      try {
        this.departmentSlotOptions = await this.$store.dispatch('serviceTypes/fetchAttachmentDepartmentOptions')
      } catch (e) {
        this.departmentSlotOptions = []
      } finally {
        this.loadingDepartmentSlots = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.gl-activity-help {
  border: 1px solid #ebe9f1;
  border-radius: 0.357rem;
  padding: 0.75rem;
}
.gl-activity-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}
.gl-activity-select.is-invalid .vs__dropdown-toggle {
  border-color: #ea5455;
}
</style>
